import { useEffect, useRef } from 'react';
import ValidationStatusBadge from './components/ValidationStatusBadge';
import ValidationProgress from './components/ValidationProgress';
import ValidationActions from './components/ValidationActions';
import ValidationList from './components/ValidationList';
import { useValidationDetails } from '../service/Hooks/useValidationDetails';
import { formatDate } from '../service/utils/formatters';
import { formatCurrency, formatCurrencys } from '../service/utils/facturationUtils';

const ValidationDetailModal = ({ 
  entityId, 
  entityType, 
  validation, 
  onClose, 
  onApprove, 
  onReject 
}) => {
  const {
    details,
    loading,
    error,
    currentUserAccountId,
    isValidationManager,
    retry
  } = useValidationDetails(entityId, entityType);

  // Référence pour éviter les re-rendus inutiles
  const hasDetailsLoaded = useRef(false);
  
  // Empêcher le rechargement si les détails sont déjà chargés
  useEffect(() => {
    if (details && !loading) {
      hasDetailsLoaded.current = true;
    }
  }, [details, loading]);

  // Utiliser les données de base si les détails ne sont pas encore chargés
  const displayData = details || (validation ? {
    entityName: validation.entityName,
    entityId: validation.entityId,
    createdUtc: validation.createdUtc,
    requesterName: validation.createdByUserName || 'Inconnu',
    globalStatus: validation.status || 'Chargement...',
    validations: [],
    orderDetails: validation.orderDetails,
    accountDetails: validation.accountDetails
  } : null);

   // Vérifier si l'utilisateur peut valider
  const canUserValidate = () => {
    if (!currentUserAccountId || !details || !isValidationManager) return false;
    
    const userValidation = details.validations?.find(
      v => v.validatedByAccountId === currentUserAccountId
    );
    
    return isValidationManager && (!userValidation || userValidation.status === 'Pending');
  };

  // Si pas de données du tout
  if (!displayData && !loading) {
    return (
      <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Données manquantes
              </h5>
              <button 
                type="button" 
                className="btn-close btn-close-white" 
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body text-center py-4">
              <div className="alert alert-danger mb-3">
                <i className="fas fa-database-x me-2"></i>
                Impossible de charger les données de validation.
              </div>
              <button className="btn btn-secondary" onClick={onClose}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header bg-white text-white">
            <div className="d-flex flex-column w-100">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="modal-title mb-0">
                  <i className="fas fa-clipboard-check me-2"></i>
                  Détails de Validation
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-primary" 
                  onClick={onClose}
                ></button>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span className="text-white-75">
                  {displayData?.entityName} #{displayData?.entityId}
                </span>
                {details ? (
                  <ValidationStatusBadge status={details.globalStatus} />
                ) : (
                  <span className="badge bg-light text-dark">
                    {validation?.status || 'Chargement...'}
                  </span>
                )}
                <small className="text-white-75">
                  {details?.validations?.length || 0} validateur(s)
                </small>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="modal-body">
            {/* États de chargement et erreur */}
            {loading && !hasDetailsLoaded.current && (
              <div className="alert alert-info d-flex align-items-center mb-3">
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                <span>Chargement des détails en cours...</span>
              </div>
            )}

            {error && (
              <div className="alert alert-danger d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <span>{error}</span>
                </div>
                <button className="btn btn-sm btn-outline-danger" onClick={retry}>
                  <i className="fas fa-arrow-clockwise me-1"></i>
                  Réessayer
                </button>
              </div>
            )}

            <div className="row">
              {/* Colonne principale - Informations */}
              <div className="col-lg-8">
                {/* Carte Informations Générales */}
                <div className="card">
                  <div className="card-header bg-white">
                    <h6 className="mb-0 text-primary">
                      <i className="bi bi-info-circle me-2"></i>
                      Informations Générales
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="mb-2">
                          <small className="text-muted d-block">ID Entité</small>
                          <strong>{displayData?.entityId}</strong>
                        </div>
                        <div className="mb-2">
                          <small className="text-muted d-block">Type d'element a valider</small>
                          <span className="badge bg-secondary bg-opacity-10 border border-opacity-25 text-secondary rounded-4">
                            {displayData?.entityName}
                          </span>
                        </div>
                        {/* details des comptes */}
                        {displayData && (displayData.orderDetails || displayData.accountDetails) && (
                          <>
                            <div className="card-header bg-white">
                              <h6 className="mb-0 text-primary">
                                <i className="bi bi-file-text me-2"></i>
                                Détails {displayData.entityName}
                              </h6>
                            </div>
                            <div className="card-body">
                              {displayData.entityName === 'Order' && displayData.orderDetails && (
                                <div className="row g-3">
                                  <div className="col-md-6">
                                    <small className="text-muted d-block">Instrument</small>
                                    <strong>{displayData.orderDetails.securityName}</strong>
                                  </div>
                                  <div className="col-md-6">
                                    <small className="text-muted d-block">Type</small>
                                    <span className={`badge ${
                                      displayData.orderDetails.side === 'Buy' 
                                        ? 'bg-success' 
                                        : 'bg-danger'
                                    }`}>
                                      {displayData.orderDetails.side}
                                    </span>
                                  </div>
                                  <div className="col-md-6">
                                    <small className="text-muted d-block">Quantité</small>
                                    <strong>{formatCurrency(displayData.orderDetails.quantity)}</strong>
                                  </div>
                                  <div className="col-md-6">
                                    <small className="text-muted d-block">Prix</small>
                                    <strong>{formatCurrencys(displayData.orderDetails.price)} Fcfa</strong>
                                  </div>
                                  <div className="col-md-6">
                                    <small className="text-muted d-block">Marché cible</small>
                                    <strong>{displayData.orderDetails.targetMarket}</strong>
                                  </div>
                                  <div className="col-md-6">
                                    <small className="text-muted d-block">Compte</small>
                                    <strong>{displayData.orderDetails.accountName}</strong>
                                  </div>
                                </div>
                              )}

                              {displayData.entityName === 'Account' && displayData.accountDetails && (
                                <div className="row g-3">
                                  <div className="col-md-6">
                                    <small className="text-muted d-block">Nom</small>
                                    <strong>
                                      {displayData.accountDetails.companyName || 
                                      `${displayData.accountDetails.firstName} ${displayData.accountDetails.lastName}`}
                                    </strong>
                                  </div>
                                  <div className="col-md-6">
                                    <small className="text-muted d-block">Email</small>
                                    <strong>{displayData.accountDetails.email}</strong>
                                  </div>
                                  <div className="col-md-6">
                                    <small className="text-muted d-block">Téléphone</small>
                                    <strong>{displayData.accountDetails.phoneNumber}</strong>
                                  </div>
                                  <div className="col-md-6">
                                    <small className="text-muted d-block">Type de compte</small>
                                    <strong>{displayData.accountDetails.accountTypeName}</strong>
                                  </div>
                                  <div className="col-md-6">
                                    <small className="text-muted d-block">Cash disponible</small>
                                    <strong className="text-success">
                                      {formatCurrencys(displayData.accountDetails.cash)} 
                                    </strong>
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                        <div className="col-md-6">
                          <div className="mb-2 mt-1">
                            <small className="text-muted d-block mb-2">Demandeur</small>
                            <strong className='bg-light bg-opacity-10 border border-opacity-25 rounded-5 text-dark fs-5 p-1 mt-1'>
                              {displayData?.validations?.[0]?.requesterName || 'N/A'}
                            </strong>
                          </div>

                          <div className="mb-2">
                            <small className="text-muted d-block">Date Soumission</small>
                            <strong>
                              {displayData?.validations?.[0]?.createdUtc
                                ? formatDate(displayData.validations[0].createdUtc)
                                : 'N/A'}
                            </strong>
                          </div>
                        </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Colonne latérale - Validation */}
              <div className="col-lg-4">
                {details ? (
                  <div className="d-flex flex-column gap-3">
                    <ValidationProgress 
                      validations={details.validations || []}
                      currentUserAccountId={currentUserAccountId}
                    />

                    {canUserValidate() && (
                      <ValidationActions
                        onApprove={onApprove}
                        onReject={onReject}
                        entityId={entityId}
                        entityType={entityType}
                        validationId={validation?.validationId}
                      />
                    )}

                    <ValidationList 
                      validations={details.validations || []}
                      currentUserAccountId={currentUserAccountId}
                    />
                  </div>
                ) : (
                  !loading && (
                    <div className="card">
                      <div className="card-body text-center text-muted py-4">
                        <i className="fas fa-hourglass-split display-6 d-block mb-2 opacity-50"></i>
                        <small>Chargement des informations de validation...</small>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidationDetailModal;